import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const WalletContext = createContext(null);

// Mock wallet addresses for demo
const DEMO_ADDRESSES = [
    '0x742d35Cc6634C0532925a3b844Bc454e4438f44E',
    '0x8ba1f109551bD432803012645Ac136ddd64DBA72',
    '0xdD2FD4581271e230360230F9337D5c0430Bf44C0',
    '0x1234567890AbcdEF1234567890AbcdEF12345678',
];

export function WalletProvider({ children }) {
    const [address, setAddress] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [balance, setBalance] = useState(0);
    const [chainId, setChainId] = useState(8453); // Base Mainnet

    // Check for saved wallet on mount
    useEffect(() => {
        const savedAddress = localStorage.getItem('paripeer_wallet');
        if (savedAddress) {
            setAddress(savedAddress);
            setIsConnected(true);
            setBalance(Math.random() * 10 + 0.5); // Mock balance
        }
    }, []);

    const connect = useCallback(async () => {
        setIsConnecting(true);
        
        // Simulate connection delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // In production, this would use RainbowKit
        // For demo, we'll use a mock wallet
        const randomAddress = DEMO_ADDRESSES[Math.floor(Math.random() * DEMO_ADDRESSES.length)];
        const mockBalance = Math.random() * 10 + 0.5;
        
        setAddress(randomAddress);
        setBalance(mockBalance);
        setIsConnected(true);
        setIsConnecting(false);
        
        localStorage.setItem('paripeer_wallet', randomAddress);
        
        return randomAddress;
    }, []);

    const disconnect = useCallback(() => {
        setAddress(null);
        setBalance(0);
        setIsConnected(false);
        localStorage.removeItem('paripeer_wallet');
    }, []);

    const switchNetwork = useCallback(async (newChainId) => {
        // Simulate network switch
        await new Promise(resolve => setTimeout(resolve, 500));
        setChainId(newChainId);
    }, []);

    // Mock transaction sending
    const sendTransaction = useCallback(async ({ to, value }) => {
        if (!isConnected) throw new Error('Wallet not connected');
        
        // Simulate transaction
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Update balance
        setBalance(prev => Math.max(0, prev - parseFloat(value)));
        
        // Return mock tx hash
        return '0x' + Array(64).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join('');
    }, [isConnected]);

    const value = {
        address,
        isConnecting,
        isConnected,
        balance,
        chainId,
        connect,
        disconnect,
        switchNetwork,
        sendTransaction,
        // Chain info
        chainName: chainId === 8453 ? 'Base' : 'Unknown',
        isMainnet: chainId === 8453,
    };

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
}

export function useWallet() {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error('useWallet must be used within a WalletProvider');
    }
    return context;
}

// Utility to format address
export function formatAddress(address) {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// Utility to format ETH
export function formatETH(amount, decimals = 4) {
    if (amount === null || amount === undefined) return '0';
    return parseFloat(amount).toFixed(decimals);
}
